import { ENV } from "../constants/environment";
import {
  getFirebaseAuthErrorDescriptionTr,
  getProviderDescriptionTr,
  getStageDescriptionTr,
} from "./clientAuthErrorLabelsTr";

/**
 * Mobil tarayıcı vs masaüstü (panel) ayrımı — ClientAuthError API üzerinden izlenebilir.
 */
function getClientAuthSourceModule() {
  if (typeof navigator === "undefined") return "Web";
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? "Web-Mobile"
    : "Web";
}

function normalizeEndpointPath(input) {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      const parsed = new URL(trimmed);
      return `${parsed.pathname}${parsed.search || ""}`;
    }
  } catch (_) {
    // URL parse edilemezse ham değere düş.
  }

  if (trimmed.startsWith("/")) return trimmed;
  return `/${trimmed}`;
}

function buildClientDetails(payload, failedEndpoint) {
  let detailsObj = {};

  if (payload?.details && typeof payload.details === "object") {
    detailsObj = { ...payload.details };
  } else if (typeof payload?.details === "string" && payload.details.trim()) {
    try {
      const parsed = JSON.parse(payload.details);
      if (parsed && typeof parsed === "object") {
        detailsObj = { ...parsed };
      } else {
        detailsObj = { rawDetails: payload.details };
      }
    } catch (_) {
      detailsObj = { rawDetails: payload.details };
    }
  }

  if (failedEndpoint) {
    detailsObj.failedEndpoint = failedEndpoint;
  }

  if (!Object.keys(detailsObj).length) return null;
  try {
    return JSON.stringify(detailsObj);
  } catch (_) {
    return null;
  }
}

/**
 * Sunucuya istemci tarafı auth hatası raporu gönderir (şifre/token gönderilmez).
 * @param {object} payload
 * @param {string} [payload.provider]
 * @param {string} payload.stage
 * @param {string|null} [payload.identifier]
 * @param {string|null} [payload.errorCode]
 * @param {string|null} [payload.errorMessage]
 * @param {Error|{code?:string,message?:string}|null} [payload.error] — errorCode/errorMessage ile birlikte kullanılabilir
 * @param {string} [payload.asamaAciklamaTr] — stage için özel Türkçe açıklama (yoksa sözlükten)
 * @param {string} [payload.hataAciklamaTr] — hata için özel Türkçe açıklama (yoksa auth kodu sözlüğünden)
 * @param {string} [payload.saglayiciAciklamaTr] — provider için özel Türkçe açıklama
 */
export async function reportClientAuthError(payload) {
  if (!payload?.stage) return;
  const err = payload.error;
  const errorCode =
    payload.errorCode ?? (err && typeof err === "object" ? err.code : null) ?? null;
  let errorMessage =
    payload.errorMessage ??
    (err && typeof err === "object" && err.message ? err.message : null) ??
    (typeof err === "string" ? err : null) ??
    "Unknown";
  if (typeof errorMessage === "string" && errorMessage.length > 2000) {
    errorMessage = errorMessage.slice(0, 2000);
  }

  const provider = payload.provider ?? "Unknown";
  const asamaAciklamaTr = getStageDescriptionTr(payload.stage, payload.asamaAciklamaTr);
  const saglayiciAciklamaTr = getProviderDescriptionTr(provider, payload.saglayiciAciklamaTr);
  const mappedFirebaseTr = getFirebaseAuthErrorDescriptionTr(errorCode, null);
  let hataAciklamaTr =
    payload.hataAciklamaTr ?? mappedFirebaseTr ?? null;
  if (!hataAciklamaTr && errorCode && String(errorCode).startsWith("auth/")) {
    hataAciklamaTr = "Firebase kimlik doğrulama hatası.";
  }
  if (!hataAciklamaTr && errorMessage && errorMessage !== "Unknown") {
    hataAciklamaTr = `Teknik özet: ${errorMessage}`.slice(0, 2000);
  }
  if (!hataAciklamaTr) {
    hataAciklamaTr = "Açıklanamayan istemci kimlik doğrulama hatası.";
  }

  const failedEndpoint = normalizeEndpointPath(
    payload?.failedEndpoint || payload?.requestUrl || payload?.apiEndpoint
  );
  const details = buildClientDetails(payload, failedEndpoint);

  const url = `${ENV.apiUrl}/api/auth/ClientAuthError`;
  const body = JSON.stringify({
    provider,
    stage: payload.stage,
    identifier: payload.identifier ?? null,
    errorCode,
    errorMessage,
    locale: "tr",
    asamaAciklamaTr,
    hataAciklamaTr,
    saglayiciAciklamaTr,
    details,
  });

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Source-Module": getClientAuthSourceModule(),
        "X-Client-OS": typeof navigator !== "undefined" ? navigator?.platform || "Unknown" : "Unknown",
        "Accept-Language": "tr",
      },
      body,
      keepalive: true,
    });
    if (!res.ok && process.env.NODE_ENV !== "production") {
      let detail = "";
      try {
        detail = await res.text();
      } catch {
        detail = "";
      }
      // eslint-disable-next-line no-console
      console.warn("[ClientAuthError] Audit raporu sunucuya yazılamadı:", res.status, url, detail?.slice?.(0, 500));
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[ClientAuthError] İstek gönderilemedi (ağ / CORS / API adresi):", url, e?.message || e);
    }
  }
}
